import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import FileIcon from "components/FileIcon";
import {
  ZipEncryptedError,
  ZipLimitExceededError,
  extractZipEntry,
  listZipEntries,
} from "./zipParser";
import type { ZipEntryInfo } from "./zipParser";
import type { PreviewTarget } from "../FilePreviewDialog";
import { formatSize } from "./formatSize";

interface ZipPreviewProps {
  buffer: ArrayBuffer;
  /** Apre una voce dell'archivio nell'anteprima del chiamante. */
  onPreviewFile: (target: PreviewTarget) => void;
}

const ZipPreview: React.FC<ZipPreviewProps> = ({ buffer, onPreviewFile }) => {
  const translations = useTranslations();
  const [entries, setEntries] = useState<ZipEntryInfo[] | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setEntries(null);
    setErrorKey(null);
    listZipEntries(buffer)
      .then((list) => {
        if (!cancelled) setEntries(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setErrorKey(
          e instanceof ZipLimitExceededError
            ? "preview.zip.limit.exceeded"
            : e instanceof ZipEncryptedError
              ? "preview.zip.error.encrypted"
              : "preview.zip.error.corrupt"
        );
      });
    return () => {
      cancelled = true;
    };
  }, [buffer]);

  const extract = async (path: string) => {
    try {
      const bytes = await extractZipEntry(buffer, path);
      const name = path.split("/").pop() || path;
      const url = URL.createObjectURL(new Blob([bytes as BlobPart]));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setErrorKey(
        e instanceof ZipEncryptedError ? "preview.zip.error.encrypted" : "preview.zip.error.corrupt"
      );
    }
  };

  /** Apre subito l'anteprima (rotella) e lascia che l'estrazione — lenta su
   *  archivi grandi — avvenga dentro il dialog annidato. */
  const preview = (path: string) => {
    const name = path.split("/").pop() || path;
    onPreviewFile({
      name,
      load: async () =>
        new File([(await extractZipEntry(buffer, path)) as BlobPart], name, {
          type: "application/octet-stream",
        }),
    });
  };

  if (errorKey) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={200}>
        <Typography color="text.secondary">
          {getTranslation(translations, errorKey) || "Archivio non leggibile."}
        </Typography>
      </Box>
    );
  }

  if (!entries) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, overflow: "auto", width: "100%" }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {getTranslation(translations, "preview.zip.contents.title") || "Contenuto dell'archivio"} ({entries.length})
      </Typography>
      <List dense>
        {entries.map((entry) => (
          <ListItem
            key={entry.path}
            disablePadding
            secondaryAction={
              <Tooltip title={getTranslation(translations, "preview.zip.entry.extract") || "Estrai file"}>
                <IconButton size="medium" sx={{ p: 0.25 }} onClick={() => extract(entry.path)}>
                  <Download fontSize="inherit" color="primary" />
                </IconButton>
              </Tooltip>
            }
          >
            <Tooltip title={getTranslation(translations, "preview.zip.entry.preview") || "Anteprima"}>
              <ListItemAvatar
                onClick={() => preview(entry.path)}
                sx={{ cursor: "pointer" }}
              >
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: "#54bdc1",
                    color: "#fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    "& .MuiSvgIcon-root": { fontSize: "2rem" },
                  }}
                >
                  <FileIcon filename={entry.path} />
                </Avatar>
              </ListItemAvatar>
            </Tooltip>
            <ListItemText primary={entry.path} secondary={formatSize(entry.size)} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default ZipPreview;
